import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { Home as HomeIcon, Settings as SettingsIcon, Ear } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import useScopaGame from '../hooks/useScopaGame';
import NeapolitanCard from '../components/NeapolitanCard';
import OpponentCaptureFx from '../components/OpponentCaptureFx';
import CardFace from '../components/cards/CardFace';
import ScorePanel from '../components/ScorePanel';
import CaptureChoice from '../components/CaptureChoice';

function TurnIndicator({ state }) {
  let text = 'È il tuo turno';
  if (state.phase === 'gameOver') text = 'Partita finita';
  else if (state.phase === 'handOver') text = 'Fine mano';
  else if (state.turn === 'cpu') text = 'Turno dell’avversario';
  const active = state.phase === 'playing' && state.turn === 'player';
  return (
    <div
      className={`px-3 py-1 rounded-full text-xs font-sans font-semibold tracking-wide ${
        active
          ? 'bg-[#D4AF37] text-[#14452F]'
          : 'bg-black/30 text-[#E0E0D1]'
      }`}
      data-testid="turn-indicator"
    >
      {text}
    </div>
  );
}

function BreakdownRow({ label, p, c, pWon, cWon }) {
  return (
    <div className="flex items-center justify-between text-sm py-1 border-b border-white/5">
      <span className={`font-sans ${pWon ? 'text-[#D4AF37] font-bold' : 'text-[#E0E0D1]'}`}>
        {p}
      </span>
      <span className="font-sans text-xs uppercase tracking-[0.1em] text-[#E0E0D1]/60">
        {label}
      </span>
      <span className={`font-sans ${cWon ? 'text-[#D4AF37] font-bold' : 'text-[#E0E0D1]'}`}>
        {c}
      </span>
    </div>
  );
}

export default function GameScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [mode] = useState(() => location.state?.mode || 'continue');

  // Consume the one-shot start intent once. history.state survives a page
  // reload, so without this a refresh on /game would re-run the 'new' branch
  // and re-deal a brand-new game, wiping the saved one. After capturing mode,
  // clear the navigation state so any reload falls through to 'continue'
  // (which resumes the game persisted by the hook's mount effect).
  useEffect(() => {
    if (location.state?.mode) {
      navigate('/game', { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    state,
    selectedCardId,
    captureOptions,
    live,
    captureFx,
    selectCard,
    chooseCapture,
    cancelSelection,
    readTable,
    continueNextHand,
    restart,
  } = useScopaGame({ mode, settings });

  const [scopaBanner, setScopaBanner] = useState(null);
  const prevScope = useRef({ player: 0, cpu: 0 });
  const handRef = useRef(null);
  const nextHandBtnRef = useRef(null);
  const playAgainBtnRef = useRef(null);

  // ---- Flying capture animation (purely visual, hidden from VoiceOver) ----
  const nodeMap = useRef(new Map()); // card id -> live DOM node
  const rectMap = useRef(new Map()); // card id -> last known bounding rect
  const cpuPileRef = useRef(null);
  const playerPileRef = useRef(null);
  const [flyers, setFlyers] = useState([]);

  const setCardNode = useCallback(
    (id) => (el) => {
      if (el) nodeMap.current.set(id, el);
      else nodeMap.current.delete(id);
    },
    []
  );

  // Cache the position of every rendered card each render so we still know
  // where a card was after the engine removes it from the table/hand.
  useLayoutEffect(() => {
    for (const [id, el] of nodeMap.current) {
      const r = el.getBoundingClientRect();
      if (r.width) rectMap.current.set(id, r);
    }
  });

  // CPU capture -> same online-style sequence (played + captured shown together,
  // then sweep toward the CPU pile). Cosmetic only; reads the existing captureFx
  // (no engine/AI/logic change). Deduped by nonce.
  const [oppFx, setOppFx] = useState(null);
  const oppFxNonce = useRef(null);
  const oppFxTimer = useRef(null);
  useEffect(() => () => clearTimeout(oppFxTimer.current), []);
  useEffect(() => {
    if (!captureFx || captureFx.actor !== 'cpu') return;
    if (oppFxNonce.current === captureFx.nonce) return;
    oppFxNonce.current = captureFx.nonce;
    clearTimeout(oppFxTimer.current);
    setOppFx({ played: captureFx.played, captured: captureFx.captured });
    oppFxTimer.current = setTimeout(() => setOppFx(null), 800);
  }, [captureFx]);

  // On a capture, fly the captured cards (+ the played card) to the pile.
  useEffect(() => {
    if (!captureFx) return;
    // CPU captures use the readable OpponentCaptureFx overlay instead of the fast
    // fly, so the player can clearly see the played + captured combination.
    if (captureFx.actor === 'cpu') return;
    const targetRef = captureFx.actor === 'player' ? playerPileRef : cpuPileRef;
    const tgt = targetRef.current && targetRef.current.getBoundingClientRect();
    if (!tgt) return;
    const cards = [...captureFx.captured, captureFx.played];
    const items = [];
    cards.forEach((card, i) => {
      let src = rectMap.current.get(card.id);
      if (!src) src = cpuPileRef.current && cpuPileRef.current.getBoundingClientRect();
      if (!src || !src.width) return;
      items.push({ key: `${captureFx.nonce}-${card.id}`, card, src, tgt, i });
    });
    if (!items.length) return;
    setFlyers(items);
    const t = setTimeout(() => setFlyers([]), 650);
    return () => clearTimeout(t);
  }, [captureFx]);

  // Dismiss any lingering toasts (e.g. "Fine della mano") when leaving the game.
  useEffect(() => () => toast.dismiss(), []);

  // Move focus into the end-of-hand / end-of-game dialog when it opens so
  // VoiceOver lands on the primary action (parity with CaptureChoice).
  useEffect(() => {
    if (state.phase === 'handOver') nextHandBtnRef.current?.focus();
    else if (state.phase === 'gameOver') playAgainBtnRef.current?.focus();
  }, [state.phase]);

  // When the capture-choice dialog closes (a choice was made or cancelled),
  // bring accessibility focus back to the board (the player hand) so VoiceOver
  // resumes on a real, current element instead of the exiting dialog.
  const prevCaptureOptions = useRef(null);
  useEffect(() => {
    if (prevCaptureOptions.current && !captureOptions) {
      handRef.current?.focus();
    }
    prevCaptureOptions.current = captureOptions;
  }, [captureOptions]);

  // Detect a Scopa to fire the special visual animation.
  useEffect(() => {
    const prev = prevScope.current;
    if (state.scope.player > prev.player) setScopaBanner('player');
    else if (state.scope.cpu > prev.cpu) setScopaBanner('cpu');
    prevScope.current = { ...state.scope };
  }, [state.scope]);

  useEffect(() => {
    if (!scopaBanner) return;
    const t = setTimeout(() => setScopaBanner(null), 1600);
    return () => clearTimeout(t);
  }, [scopaBanner]);

  const playerTurn = state.phase === 'playing' && state.turn === 'player';
  const b = state.lastHandBreakdown;
  // While any overlay (capture choice / hand-over / game-over) is open, make
  // the board behind it inert so VoiceOver / keyboard cannot reach it.
  const overlayOpen =
    !!captureOptions || state.phase === 'handOver' || state.phase === 'gameOver';

  // Keep Tab focus inside an open dialog (the board behind is already inert).
  const trapTab = (e) => {
    if (e.key !== 'Tab') return;
    const focusables = e.currentTarget.querySelectorAll(
      'button, [href], [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="relative h-[100dvh] w-full overflow-hidden felt-bg flex justify-center"
      data-testid="game-screen"
    >
      {/* Visually hidden live region for VoiceOver / status */}
      <div className="sr-only" aria-live="polite" role="status" data-testid="live-region">
        {live}
      </div>

      {/* CPU capture sequence — cosmetic only, aria-hidden, no pointer/focus */}
      <OpponentCaptureFx fx={oppFx} />

      {/* Invisible pile anchors for the flying-capture animation */}
      <div ref={cpuPileRef} aria-hidden="true" className="pointer-events-none absolute left-1/2 top-24 w-14 h-20 -translate-x-1/2 opacity-0" />
      <div ref={playerPileRef} aria-hidden="true" className="pointer-events-none absolute left-1/2 bottom-6 w-14 h-20 -translate-x-1/2 opacity-0" />

      <div className="w-full max-w-md flex flex-col h-full px-3 pt-3 pb-2" inert={overlayOpen ? true : undefined}>
        {/* Top bar */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <button
            type="button"
            onClick={() => navigate('/')}
            data-testid="exit-btn"
            aria-label="Torna alla schermata iniziale"
            className="w-9 h-9 rounded-full bg-[#2C1A14] border border-[#D4AF37]/30 flex items-center justify-center text-[#FDFBF7] transition-colors hover:bg-[#3D261E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50"
          >
            <HomeIcon className="w-4 h-4" />
          </button>
          <TurnIndicator state={state} />
          <button
            type="button"
            onClick={() => navigate('/settings')}
            data-testid="game-settings-btn"
            aria-label="Impostazioni"
            className="w-9 h-9 rounded-full bg-[#2C1A14] border border-[#D4AF37]/30 flex items-center justify-center text-[#FDFBF7] transition-colors hover:bg-[#3D261E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50"
          >
            <SettingsIcon className="w-4 h-4" />
          </button>
        </div>

        <ScorePanel state={state} />

        {/* Opponent hand */}
        <div className="mt-3 flex items-center justify-center gap-1.5" aria-label={`L'avversario ha ${state.hands.cpu.length} carte in mano`} data-testid="opponent-hand">
          {state.hands.cpu.map((c, i) => (
            <NeapolitanCard key={c.id} faceDown sizeClass="w-11 sm:w-12" className="opacity-95" testId={`opponent-card-${i}`} />
          ))}
          {state.hands.cpu.length === 0 && (
            <div className="h-16" aria-hidden="true" />
          )}
        </div>

        {/* Table */}
        <div className="flex-1 relative flex flex-col items-center justify-center my-2">
          <div
            className="w-full flex flex-wrap items-center justify-center gap-2 content-center min-h-[7rem] rounded-2xl bg-black/10 p-3 border border-[#D4AF37]/10"
            data-testid="table-area"
            aria-label="Tavolo da gioco"
          >
            <AnimatePresence>
              {state.table.length === 0 ? (
                <motion.p
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="font-sans text-sm text-[#E0E0D1]/50 italic"
                >
                  Tavolo vuoto
                </motion.p>
              ) : (
                state.table.map((c) => (
                  <motion.div
                    key={c.id}
                    ref={setCardNode(c.id)}
                    aria-hidden="true"
                    layout
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.12 } }}
                  >
                    <NeapolitanCard card={c} sizeClass="w-14 sm:w-16" testId={`table-card-${c.id}`} />
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>

          {settings.accessibleMode && (
            <div
              role="text"
              tabIndex={0}
              onClick={readTable}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  readTable();
                }
              }}
              data-testid="read-table-btn"
              aria-label="Leggi tavolo"
              className="mt-3 flex items-center gap-2 rounded-full bg-[#2C1A14] border border-[#D4AF37]/40 text-[#FDFBF7] font-sans font-semibold text-sm px-4 py-2 cursor-pointer transition-colors hover:bg-[#3D261E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#F2D675]/60"
            >
              <Ear className="w-4 h-4 text-[#D4AF37]" aria-hidden="true" />
              Leggi tavolo
            </div>
          )}
        </div>

        {/* Player hand */}
        <div
          ref={handRef}
          tabIndex={-1}
          className="flex items-end justify-center gap-2 sm:gap-3 pb-1 min-h-[6.5rem] outline-none"
          data-testid="player-hand"
          aria-label="La tua mano"
        >
          {state.hands.player.map((c) => (
            <div key={c.id} ref={setCardNode(c.id)}>
              <NeapolitanCard
                card={c}
                interactive
                disabled={!playerTurn}
                selected={selectedCardId === c.id}
                onClick={() => selectCard(c.id)}
                sizeClass="w-16 sm:w-20"
                testId={`player-card-${c.id}`}
              />
            </div>
          ))}
          {state.hands.player.length === 0 && (
            <p className="font-sans text-sm text-[#E0E0D1]/50 italic pb-6">
              In attesa delle carte…
            </p>
          )}
        </div>
      </div>

      {/* Capture choice */}
      <AnimatePresence>
        {captureOptions && (
          <CaptureChoice
            options={captureOptions}
            onChoose={chooseCapture}
            onCancel={cancelSelection}
          />
        )}
      </AnimatePresence>

      {/* Scopa banner */}
      <AnimatePresence>
        {scopaBanner && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="scopa-banner"
          >
            <motion.div
              initial={{ scale: 0.2, rotate: -8 }}
              animate={{ scale: 1, rotate: -3 }}
              transition={{ type: 'spring', stiffness: 260, damping: 14 }}
              className="font-serif text-6xl sm:text-7xl font-bold text-[#D4AF37] drop-shadow-[0_0_30px_rgba(212,175,55,0.7)]"
            >
              SCOPA!
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hand over modal */}
      <AnimatePresence>
        {state.phase === 'handOver' && b && (
          <motion.div
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm p-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="hand-over-modal"
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="hand-over-title"
              onKeyDown={trapTab}
              className="w-full max-w-sm rounded-2xl bg-[#1c2a20] border border-[#D4AF37]/30 p-5"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
            >
              <h2 id="hand-over-title" className="font-serif text-2xl text-[#FDFBF7] text-center mb-1">
                Fine della mano
              </h2>
              <div className="flex items-center justify-around my-3">
                <div className="text-center">
                  <p className="text-xs uppercase tracking-wider text-[#E0E0D1]/70">Tu</p>
                  <p className="font-serif text-4xl font-bold text-[#D4AF37]">{state.matchScores.player}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs uppercase tracking-wider text-[#E0E0D1]/70">Avversario</p>
                  <p className="font-serif text-4xl font-bold text-[#FDFBF7]">{state.matchScores.cpu}</p>
                </div>
              </div>
              <div className="mt-2" aria-label="Dettaglio punti della mano">
                <BreakdownRow label="Scope" p={b.player.scope} c={b.cpu.scope} pWon={b.player.scope > 0} cWon={b.cpu.scope > 0} />
                <BreakdownRow label="Carte" p={b.player.carte} c={b.cpu.carte} pWon={b.player.won.carte} cWon={b.cpu.won.carte} />
                <BreakdownRow label="Denari" p={b.player.denari} c={b.cpu.denari} pWon={b.player.won.denari} cWon={b.cpu.won.denari} />
                <BreakdownRow label="Settebello" p={b.player.settebello ? 'Sì' : 'No'} c={b.cpu.settebello ? 'Sì' : 'No'} pWon={b.player.won.settebello} cWon={b.cpu.won.settebello} />
                <BreakdownRow label="Primiera" p={b.player.primiera} c={b.cpu.primiera} pWon={b.player.won.primiera} cWon={b.cpu.won.primiera} />
              </div>
              <button
                type="button"
                ref={nextHandBtnRef}
                onClick={continueNextHand}
                data-testid="next-hand-btn"
                className="mt-5 w-full rounded-full bg-[#D4AF37] text-[#14452F] font-sans font-bold py-3 transition-colors hover:bg-[#F2D675] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#F2D675]/60"
              >
                Continua
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Game over modal */}
      <AnimatePresence>
        {state.phase === 'gameOver' && (
          <motion.div
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-md p-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="game-over-modal"
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="game-over-title"
              onKeyDown={trapTab}
              className="w-full max-w-sm rounded-2xl bg-[#1c2a20] border border-[#D4AF37]/40 p-6 text-center"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
            >
              <h2 id="game-over-title" className="font-serif text-4xl font-bold text-[#D4AF37] mb-2" data-testid="game-over-title">
                {state.winner === 'player' ? 'Hai vinto!' : 'Hai perso'}
              </h2>
              <p className="font-sans text-[#E0E0D1] mb-5">
                Punteggio finale — Tu {state.matchScores.player}, Avversario {state.matchScores.cpu}.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  ref={playAgainBtnRef}
                  onClick={restart}
                  data-testid="play-again-btn"
                  className="w-full rounded-full bg-[#D4AF37] text-[#14452F] font-sans font-bold py-3 transition-colors hover:bg-[#F2D675] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#F2D675]/60"
                >
                  Gioca ancora
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  data-testid="game-over-home-btn"
                  className="w-full rounded-full bg-[#2C1A14] border border-[#D4AF37]/40 text-[#FDFBF7] font-sans font-semibold py-3 transition-colors hover:bg-[#3D261E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50"
                >
                  Home
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Flying capture cards — decorative only, hidden from VoiceOver */}
      <div aria-hidden="true">
        {flyers.map((f) => {
          const w = f.src.width;
          const endX = f.tgt.left + f.tgt.width / 2 - w / 2;
          const endY = f.tgt.top + f.tgt.height / 2 - f.src.height / 2;
          return (
            <motion.div
              key={f.key}
              className="fixed left-0 top-0 z-30 pointer-events-none"
              style={{ width: w }}
              initial={{ x: f.src.left, y: f.src.top, opacity: 1, scale: 1, rotate: 0 }}
              animate={{ x: endX, y: endY, opacity: 0.6, scale: 0.7, rotate: f.i % 2 ? 10 : -10 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: f.i * 0.04 }}
            >
              <CardFace card={f.card} sizeClass="w-full" felt />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
